import { useEffect, useState } from "react";
import axios from "axios";
import { Clapperboard, Play } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const youtubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
};

export default function VideoSection() {
  const [cfg, setCfg] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/video`).then((r) => setCfg(r.data)).catch(() => setCfg({ video_url: null }));
  }, []);

  const videoUrl = cfg?.video_url || "";
  const yt = youtubeId(videoUrl);
  const hasVideo = Boolean(videoUrl);
  const poster = cfg?.poster_url || (yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : "/images/author-reading-classroom.jpg");
  const showPlayer = hasVideo && (playing || cfg?.autoplay);

  return (
    <section data-testid="video-section" className="py-24 sm:py-28 bg-cream">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-12">
          <Eyebrow className="mb-4">Watch</Eyebrow>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut">
            See Damali in action
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-sm sm:text-base text-walnutsoft leading-relaxed">
            Live from a Bailey event.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="video-frame"
            className="relative aspect-video rounded-[2rem] overflow-hidden border border-sand bg-walnut shadow-[0_44px_80px_-32px_rgba(49,67,92,0.45)]"
          >
            {!hasVideo && (
              <div data-testid="video-placeholder" className="absolute inset-0">
                <img
                  src="/images/author-reading-classroom.jpg"
                  alt="Damali Allison reading to children at an event"
                  className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-walnut/40 flex flex-col items-center justify-center text-center gap-3 px-6">
                  <span className="inline-flex w-16 h-16 rounded-full bg-cream/15 border border-cream/30 items-center justify-center">
                    <Clapperboard size={26} className="text-cream" />
                  </span>
                  <p className="font-display font-bold text-cream text-lg sm:text-xl">Event footage coming soon</p>
                  <p className="text-cream/70 text-sm max-w-xs">A 30-second highlights clip from Damali's latest event will play here.</p>
                </div>
              </div>
            )}

            {hasVideo && !showPlayer && (
              <button
                type="button"
                data-testid="video-play-button"
                onClick={() => setPlaying(true)}
                className="absolute inset-0 w-full h-full group cursor-pointer"
                aria-label="Play video"
              >
                <img src={poster} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-70" />
                <img src={poster} alt="Video preview" className="absolute inset-0 w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-700" />
                <span className="absolute inset-0 bg-walnut/25 group-hover:bg-walnut/15 transition-colors" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-20 h-20 rounded-full bg-terracotta text-cream flex items-center justify-center shadow-[0_20px_40px_-12px_rgba(79,163,227,0.8)] group-hover:scale-110 transition-transform duration-300">
                    <Play size={30} fill="currentColor" className="ml-1" />
                  </span>
                </span>
              </button>
            )}

            {showPlayer &&
              (yt ? (
                <iframe
                  data-testid="video-player"
                  src={`https://www.youtube.com/embed/${yt}?autoplay=1${cfg.autoplay && !playing ? "&mute=1" : ""}&rel=0`}
                  title="Damali Allison event video"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              ) : (
                <div className="absolute inset-0" data-testid="video-player">
                  <img src={poster} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-70" />
                  <video
                    src={videoUrl}
                    controls
                    autoPlay
                    muted={cfg.autoplay && !playing}
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                </div>
              ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
